import React, { useEffect, useState, useCallback, version } from "react";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import apiService from "../services/apiService";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import { Menu, Popover, Switch, Tooltip } from "@mui/material";

import {
  Box,
  Button,
  TextField,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  Container,
  Paper,
  TableContainer,
  CssBaseline,
  DialogContent,
  DialogActions,
  MenuItem,
  ThemeProvider,
  Typography,
  Alert,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import CommonPagination from "./Pagination";

export default function ReusableTab({ type }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);

  const [clients, setClients] = useState([]);
  const [envs, setEnvs] = useState([]);
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [connectors, setConnectors] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [filters, setFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const getColumnLabel = (col) => {
    const columnMap = {
      client_name: "Client",
      env_name: "Environment",
      app_name: "Application",
      job_id: "Job ID",
      jobs_script: "Process Name",
      end_point: "Endpoint",
      playbook_id: "Playbook",
      connector: "Connector",
      connection: "Connection",
      version: "Version",
    };

    return columnMap[col] || col.replace(/_/g, " ");
  };
  const getColumns = () => {
    let baseColumns = [
      "client_name",
      "env_name",
      "app_name",
      "job_id",
      "jobs_script",
    ];

    if (type === "connectors") {
      baseColumns.push("connector", "connection", "version");
    }

    if (type === "endpoints") {
      baseColumns.push("end_point");
    }

    if (type === "playbook") {
      baseColumns.push("playbook_id");
    }

    return baseColumns;
  };

  const columns = getColumns();
  useEffect(() => {
    const initialVisibility = {};
    columns.forEach((col) => {
      initialVisibility[col] = true;
    });
    setVisibleColumns(initialVisibility);
  }, [type]);

  const handleEdit = async (row) => {
    

    setEdit(true);
    setOpen(true);

    // 1️ Clients (should return user's client)
    const clientsData = await apiService.get(
      `/job-meta/dropdown?level=clients`,
    );
    setClients(clientsData);

    //  Envs (for the user's client)
    const envsData = await apiService.get(
      `/job-meta/dropdown?level=envs&cl_id=${row.cl_id}`,
    );
    setEnvs(envsData);

    //  Apps (for the selected environment)
    const appsData = await apiService.get(
      `/job-meta/dropdown?level=apps&cl_id=${row.cl_id}&env_id=${row.env_id}`,
    );
    setApps(appsData);

    //  Jobs (for the selected application)
    const jobsData = await apiService.get(
      `/job-meta/dropdown?level=jobs&cl_id=${row.cl_id}&env_id=${row.env_id}&app_id=${row.app_id}`,
    );
    setJobs(jobsData);

    //  TAB-SPECIFIC DROPDOWNS
    const baseParams = `cl_id=${row.cl_id}&env_id=${row.env_id}&app_id=${row.app_id}`;

    if (type === "connectors") {
      const connectorsData = await apiService.get(
        `/job-meta/dropdown?level=connectors&${baseParams}`,
      );
      setConnectors(connectorsData);
    }

    if (type === "endpoints") {
      const endpointsData = await apiService.get(
        `/job-meta/dropdown?level=endpoints&${baseParams}`,
      );
      setEndpoints(endpointsData);
    }

    if (type === "playbook") {
      const playbooksData = await apiService.get(
        `/job-meta/dropdown?level=playbooks&${baseParams}`,
      );
      setPlaybooks(playbooksData);
    }

    //  Map DB → UI fields correctly
    setFormData({
      ...row,
      client_name: row.client_name, // Keep the client name for display
      connector_name: row.connector,
      end_points: row.end_point,
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const j = await apiService.get(
        `/job-meta/list?type=${type}&search=${search}&page=${page}`,
      );
      
      setData(j.data || []);
      setTotal(j.total || 0);
      setTotalPages(Math.ceil((j.total || 0) / 10));
    } catch (error) {
      setErrorMessage("Failed to load data");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoading(false);
    }
  }, [type, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Reset everything when tab changes
    setFormErrors({});
    setFormData({});
    setSuccessMessage("");
    setErrorMessage("");
    setSearchTerm("");
    setFilters({});
    setTempFilters({});
    setOpen(false);
    setEdit(false);
    setOpenDeleteDialog(false);
    setDeleteRow(null);
  }, [type]);
  const openAdd = async () => {
    // Get user's client info
    const clientsData = await apiService.get(
      `/job-meta/dropdown?level=clients`,
    );
    setClients(clientsData);

    // Pre-select first client (should be user's client) and load environments
    const userClient =
      Array.isArray(clientsData) && clientsData.length > 0
        ? clientsData[0]
        : null;

    if (userClient) {
      setFormData({
        cl_id: userClient.cl_id,
        client_name: userClient.name,
      });

      // Load environments for the user's client
      const envsData = await apiService.get(
        `/job-meta/dropdown?level=envs&cl_id=${userClient.cl_id}`,
      );
      setEnvs(envsData);
    }

    setEdit(false);
    setOpen(true);
    setApps([]);
    setJobs([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((p) => ({ ...p, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "env_id") {
      const appsData = await apiService.get(
        `/job-meta/dropdown?level=apps&cl_id=${formData.cl_id}&env_id=${value}`,
      );
      setApps(appsData);
      setJobs([]);

      // Clear app_id and job_id when environment changes
      setFormData((p) => ({
        ...p,
        env_id: value,
        app_id: "",
        job_id: "",
        jobs_script: "",
      }));
    }

    if (name === "app_id") {
      const jobsData = await apiService.get(
        `/job-meta/dropdown?level=jobs&cl_id=${formData.cl_id}&env_id=${formData.env_id}&app_id=${value}`,
      );
      setJobs(jobsData);

      // Clear job_id when application changes
      setFormData((p) => ({
        ...p,
        app_id: value,
        job_id: "",
        jobs_script: "",
      }));

      // Load type-specific dropdowns
      const baseParams = `cl_id=${formData.cl_id}&env_id=${formData.env_id}&app_id=${value}`;

      if (type === "connectors") {
        const connectorsData = await apiService.get(
          `/job-meta/dropdown?level=connectors&${baseParams}`,
        );
        setConnectors(connectorsData);
      }

      if (type === "endpoints") {
        const endpointsData = await apiService.get(
          `/job-meta/dropdown?level=endpoints&${baseParams}`,
        );
        setEndpoints(endpointsData);
      }

      if (type === "playbook") {
        const playbooksData = await apiService.get(
          `/job-meta/dropdown?level=playbooks&${baseParams}`,
        );
        setPlaybooks(playbooksData);
      }
    }

    if (name === "job_id") {
      const selected = Array.isArray(jobs)
        ? jobs.find((j) => j.job_id === value)
        : null;
      setFormData((p) => ({
        ...p,
        job_id: value,
        jobs_script: selected?.jobs_script || "",
      }));
    }

    if (name === "connector_name") {
      const detailsData = await apiService.get(
        `/job-meta/dropdown?level=connector_details&cl_id=${formData.cl_id}&env_id=${formData.env_id}&app_id=${formData.app_id}&connector_name=${value}`,
      );

      setFormData((p) => ({
        ...p,
        connector_name: value,
        connection: detailsData[0]?.connection || "",
        version: detailsData[0]?.version || "",
      }));
    }
  };

  const save = async () => {
    // Simple validation (client is pre-selected, no need to validate)
    let errors = {};

    if (!formData.env_id) errors.env_id = "Environment is required";
    if (!formData.app_id) errors.app_id = "Application is required";
    if (!formData.job_id) errors.job_id = "Job is required";

    if (type === "connectors" && !formData.connector_name) {
      errors.connector_name = "Connector is required";
    }

    if (type === "endpoints" && !formData.end_points) {
      errors.end_points = "Endpoint is required";
    }

    if (type === "playbook" && !formData.playbook_id) {
      errors.playbook_id = "Playbook is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    try {
      

      let response;
      if (edit) {
        response = await apiService.put(`/job-meta/update`, {
          type,
          payload: formData,
        });
      } else {
        response = await apiService.post(`/job-meta/add`, {
          type,
          payload: formData,
        });
      }

      // Show success message from backend or default message
      const successMsg =
        response?.message || `${edit ? "Updated" : "Added"} successfully!`;
      setSuccessMessage(successMsg);
      setTimeout(() => setSuccessMessage(""), 3000);
      setOpen(false);
      load(); // reload table
    } catch (err) {
      console.error(err);

      let errorMsg = `Failed to ${edit ? "update" : "add"}`;

      if (
        err.message.includes("Duplicate key constraint violation") ||
        err.message.includes("already exists")
      ) {
        errorMsg =
          "A record with these values already exists. Please check your input and try again.";
      } else if (err.message.includes("Server error")) {
        errorMsg =
          "Server connection error. Please check if the server is running.";
      } else {
        errorMsg += `: ${err.message}`;
      }

      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleDelete = (row) => {
    setDeleteRow(row);
    setOpenDeleteDialog(true);
  };
  const confirmDelete = async () => {
    if (!deleteRow) return;

    try {
      const response = await apiService.delete(
        `/job-meta/delete?type=${type}&cl_id=${deleteRow.cl_id}&env_id=${deleteRow.env_id}&app_id=${deleteRow.app_id}&job_id=${deleteRow.job_id}&reference_no_1=${deleteRow.reference_no_1}`,
      );

      const successMsg = response?.message || "Record deleted successfully!";
      setSuccessMessage(successMsg);
      setTimeout(() => setSuccessMessage(""), 3000);

      setOpenDeleteDialog(false);
      setDeleteRow(null);
      load();
    } catch (err) {
      setErrorMessage(`Delete failed: ${err.message || "Unknown error"}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleToggleFlag = async (row) => {
    try {
      const newActiveFlag = row.active_flag === 1 ? 0 : 1;
      
      await apiService.put(`/job-meta/toggle-flag`, {
        type,
        cl_id: row.cl_id,
        env_id: row.env_id,
        app_id: row.app_id,
        job_id: row.job_id,
        reference_no_1: row.reference_no_1,
        active_flag: newActiveFlag,
      });

      // Get the type name with proper capitalization
      const typeName = type === "playbook" ? "Playbook" : type.charAt(0).toUpperCase() + type.slice(1);
      const action = newActiveFlag === 1 ? "Activated" : "Deactivated";
      const message = `${typeName} ${action}`;
      
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
      
      load(); // Reload table data
    } catch (err) {
      setErrorMessage(`Toggle failed: ${err.message || "Unknown error"}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const getPageTitle = () => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };
  const filteredData = data
    .filter((row) =>
      Object.entries(filters).every(([key, value]) =>
        value === ""
          ? true
          : row[key]?.toString().toLowerCase().includes(value.toLowerCase()),
      ),
    )
    .filter((row) =>
      searchTerm.trim() === ""
        ? true
        : Object.values(row)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
    );

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Page Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="700"
          color="#454545"
          sx={{ mb: 0 }}
        >
         
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
           <TextField
              label="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 200,
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              }}
            />
             <Button
              onClick={openAdd}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>


          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Column Customization */}
            <Tooltip title="Column Customization">
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "6px",
                  "&:hover": { color: "#1976d2" },
                }}
              >
                <SettingsIcon width={17} height={17} />
              </IconButton>
            </Tooltip>
           
            {/* Add + */}
           
           
            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * 10 + 1}-${Math.min(
                page * 10,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= totalPages}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 220,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {columns.map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "bold",
                }}
              >
                {getColumnLabel(col)}
              </Typography>

              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2DB0E4",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mt: 2,
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 250px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table stickyHeader sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Actions</TableCell>

                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {getColumnLabel(col)}

                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  setFilterAnchorEls((prev) => ({
                                    ...prev,
                                    [col]: e.currentTarget,
                                  }))
                                }
                              >
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() =>
                                setFilterAnchorEls((prev) => ({
                                  ...prev,
                                  [col]: null,
                                }))
                              }
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent sx={{ p: 2, width: 250 }}>
                                <TextField
                                  label={`Filter ${col.replace(/_/g, " ")}`}
                                  value={tempFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                  size="small"
                                />

                                <DialogActions sx={{ mt: 1 }}>
                                  <Button
                                    onClick={() => {
                                      setTempFilters((prev) => ({
                                        ...prev,
                                        [col]: "",
                                      }));
                                      setFilters((prev) => ({
                                        ...prev,
                                        [col]: "",
                                      }));
                                      setFilterAnchorEls((prev) => ({
                                        ...prev,
                                        [col]: null,
                                      }));
                                    }}
                                  >
                                    Clear
                                  </Button>

                                  <Button
                                    variant="contained"
                                    onClick={() => {
                                      setFilters((prev) => ({
                                        ...prev,
                                        [col]: tempFilters[col] || "",
                                      }));
                                      setFilterAnchorEls((prev) => ({
                                        ...prev,
                                        [col]: null,
                                      }));
                                    }}
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    filteredData.map((r, i) => (
                      <TableRow
                        key={r.reference_no_1 || i}
                        sx={{ height: "35px" }}
                      >
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                            flexWrap="nowrap"
                            sx={{ minWidth: "max-content" }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggleFlag(r)}
                              title="Toggle Active Flag"
                            >
                              <FlagIcon
                                style={{
                                  color: r.active_flag === 1 ? "#00CC22" : "#FF0C0C",
                                  width: 17,
                                  height: 17,
                                  transition: "color 0.2s",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => handleEdit(r)}
                              size="small"
                              sx={{ color: "#2DB0E4" }}
                              title="Edit"
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(r)}
                              size="small"
                              sx={{ color: "error.main" }}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {columns.map(
                          (col) =>
                            visibleColumns[col] && (
                              <TableCell key={col}>{r[col]}</TableCell>
                            ),
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No {type} data found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1.5,
              pr: 2,
            }}
          ></Box>
        </ThemeProvider>
      </Box>
      <ThemeProvider theme={theme}>
        <Dialog
          maxWidth="md"
          fullWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              width: "80%",
              maxWidth: "900px",
            },
          }}
          open={open}
          onClose={() => setOpen(false)}
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              py: 1,
            }}
          >
            {edit ? "Edit" : "Add"}{" "}
            {type ? type.charAt(0).toUpperCase() + type.slice(1) : ""}
            <IconButton
              // onClick={() => setOpen(false)}
              onClick={() => {
                setOpen(false);
                setFormErrors({});
              }}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 1.1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
                mt: 1,
              }}
            >
              {/* CLIENT */}
              <TextField
                label="Client"
                value={formData.client_name || ""}
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },

                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#666", // text color
                    cursor: "not-allowed",
                  },

                  "& .MuiInputLabel-root": {
                    color: "#666 !important",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ccc",
                  },
                }}
              />
              {/* ENV */}
              <TextField
                select
                label="Environment"
                name="env_id"
                value={formData.env_id || ""}
                onChange={handleChange}
                required
                error={!!formErrors.env_id}
                helperText={formErrors.env_id}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        width: "auto",
                      },
                    },
                  },
                }}
              >
                {Array.isArray(envs) &&
                  envs.map((e) => (
                    <MenuItem
                      key={e.env_id}
                      value={e.env_id}
                      sx={{
                        fontSize: "0.875rem",
                        minHeight: "32px",
                        padding: "4px 8px",
                      }}
                    >
                      {e.name}
                    </MenuItem>
                  ))}
              </TextField>

              {/* APP */}
              <TextField
                select
                label="Application"
                name="app_id"
                value={formData.app_id || ""}
                onChange={handleChange}
                required
                error={!!formErrors.app_id}
                helperText={formErrors.app_id}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        width: "auto",
                      },
                    },
                  },
                }}
              >
                {Array.isArray(apps) &&
                  apps.map((a) => (
                    <MenuItem
                      key={a.app_id}
                      value={a.app_id}
                      sx={{
                        fontSize: "0.875rem",
                        minHeight: "32px",
                        padding: "4px 8px",
                      }}
                    >
                      {a.name}
                    </MenuItem>
                  ))}
              </TextField>

              {/* JOB ID */}
              <TextField
                select
                label="Job ID"
                name="job_id"
                value={formData.job_id || ""}
                onChange={handleChange}
                required
                error={!!formErrors.job_id}
                helperText={formErrors.job_id}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        width: "auto",
                      },
                    },
                  },
                }}
              >
                {Array.isArray(jobs) &&
                  jobs.map((j) => (
                    <MenuItem
                      key={j.job_id}
                      value={j.job_id}
                      sx={{
                        fontSize: "0.875rem",
                        minHeight: "32px",
                        padding: "4px 8px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "300px",
                      }}
                    >
                      {j.job_id} - {j.jobs_script}
                    </MenuItem>
                  ))}
              </TextField>

              {/* PROCESS NAME */}
              <TextField
                label="Process Name"
                value={formData.jobs_script || ""}
                InputProps={{ readOnly: true }}
              />

              {/* TAB BASED FIELDS */}
              {type === "connectors" && (
                <>
                  <TextField
                    select
                    label="Connector"
                    name="connector_name"
                    value={formData.connector_name || ""}
                    onChange={handleChange}
                    required
                    error={!!formErrors.connector_name}
                    helperText={formErrors.connector_name}
                  >
                    {Array.isArray(connectors) &&
                      connectors.map((c) => (
                        <MenuItem
                          key={c.connector_name}
                          value={c.connector_name}
                          sx={{
                            fontSize: "0.875rem",
                            minHeight: "32px",
                            padding: "4px 8px",
                          }}
                        >
                          {c.connector_name}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    label="Connection"
                    value={formData.connection || ""}
                    disabled
                  />
                  <TextField
                    label="Version"
                    value={formData.version || ""}
                    disabled
                  />
                </>
              )}

              {type === "endpoints" && (
                <TextField
                  select
                  label="Endpoint"
                  value={formData.end_points || ""}
                  error={!!formErrors.end_points}
                  helperText={formErrors.end_points}
                  required
                  onChange={(e) =>
                    setFormData({ ...formData, end_points: e.target.value })
                  }
                >
                  {Array.isArray(endpoints) &&
                    endpoints.map((e) => (
                      <MenuItem
                        key={e.end_points}
                        value={e.end_points}
                        sx={{
                          fontSize: "0.875rem",
                          minHeight: "32px",
                          padding: "4px 8px",
                        }}
                      >
                        {e.end_points}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {type === "playbook" && (
                <TextField
                  select
                  label="Playbook"
                  value={formData.playbook_id || ""}
                  error={!!formErrors.playbook_id}
                  helperText={formErrors.playbook_id}
                  required
                  onChange={(e) =>
                    setFormData({ ...formData, playbook_id: e.target.value })
                  }
                >
                  {Array.isArray(playbooks) &&
                    playbooks.map((p) => (
                      <MenuItem
                        key={p.playbook_id}
                        value={p.playbook_id}
                        sx={{
                          fontSize: "0.875rem",
                          minHeight: "32px",
                          padding: "4px 8px",
                        }}
                      >
                        {p.playbook_id}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save}>
              {edit ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete{" "}
              <strong>
                {deleteRow?.job_id} - {deleteRow?.jobs_script}
              </strong>
              ?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={confirmDelete}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Container>
  );
}
